import React, { useEffect, useState } from 'react';
import { navigate } from './EventsList';
import {
  authFetch,
  BACKEND_URL,
  deleteEvent,
  fetchDownloadToken,
  setEventCompressed,
  updateEvent,
} from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import { SessionBadge } from './SessionBadge';
import { EventStats } from './EventStats';
import './EventsList.css';


interface EventDetailDto {
  id: string;
  title: string;
  trackName: string;
  host: string;
  wsPort: number;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  lapCount: number;
  logFilePath: string | null;
  logFileSize: number | null;
  logCompressed: boolean;
  hasLog: boolean;
  excludeFromRankings: boolean;
  rankedAt: string | null;
}

interface RawTeamUpdateEntry {
  t: number;
  kind: string;
  data: unknown;
}

interface RawTeamUpdatesPage {
  total: number;
  offset: number;
  limit: number;
  fileSize: number | null;
  logFileName: string | null;
  entries: RawTeamUpdateEntry[];
}

interface LapRecord {
  id: string;
  teamId: string;
  kartNumber: string;
  driver: string;
  category: string;
  position: string;
  lapNumber: number;
  lapTime: string;
  sector1: string;
  sector2: string;
  sector3: string;
  gap: string;
  interval: string;
  bestLap: string;
  recordedAt: string;
}

/**
 * The lap table is a sample, not the record: an endurance event holds tens of
 * thousands of laps and rendering them all hung the page. Whole-race numbers
 * come from the stats endpoint above it instead.
 */
const LAP_SAMPLE_LIMIT = 500;

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatTs(t: number): string {
  if (!t) return '—';
  const d = new Date(t);
  if (isNaN(d.getTime())) return String(t);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

const RAW_PAGE_SIZE = 200;

interface EventDetailProps {
  eventId: string;
}

function summariseEntry(entry: RawTeamUpdateEntry): string {
  const d = entry.data as Record<string, unknown> | null;
  const kind = entry.kind || 'teamUpdate';

  if (kind === 'apexLog' && d && typeof d === 'object') {
    const type = typeof d.type === 'string' ? d.type : 'log';
    const content = typeof d.content === 'string' ? d.content : '';
    return `[${type}] ${content}`.trim();
  }
  if (kind === 'apexConnected') return 'Apex connected';
  if (kind === 'apexDisconnected') {
    const reason =
      d && typeof d === 'object' && typeof (d as { reason?: unknown }).reason === 'string'
        ? ` — ${(d as { reason: string }).reason}`
        : '';
    return `Apex disconnected${reason}`;
  }
  if (kind === 'unparseable') return 'unparseable line';

  if (!d || typeof d !== 'object') return 'teamUpdate';
  const kart = typeof d.kartNumber === 'string' && d.kartNumber ? `#${d.kartNumber}` : '';
  const driver = typeof d.driver === 'string' && d.driver ? ` ${d.driver}` : '';
  const pos = typeof d.position === 'string' && d.position ? ` P${d.position}` : '';
  const last = typeof d.lastLap === 'string' && d.lastLap ? ` lap ${d.lastLap}` : '';
  const best = typeof d.bestLap === 'string' && d.bestLap ? ` best ${d.bestLap}` : '';
  const combined = `${kart}${driver}${pos}${last}${best}`.trim();
  return combined || 'teamUpdate';
}

export const EventDetail: React.FC<EventDetailProps> = ({ eventId }) => {
  const [event, setEvent] = useState<EventDetailDto | null>(null);
  const [laps, setLaps] = useState<LapRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rawOpen, setRawOpen] = useState(false);
  const [rawPage, setRawPage] = useState<RawTeamUpdatesPage | null>(null);
  const [rawOffset, setRawOffset] = useState(0);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState<string | null>(null);
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: '', trackName: '', host: '' });
  const [busy, setBusy] = useState<null | 'save' | 'compress' | 'delete' | 'rankings'>(null);
  const [manageError, setManageError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const beginEdit = (): void => {
    if (!event) return;
    setDraft({
      title: event.title,
      trackName: event.trackName,
      host: event.host,
    });
    setManageError(null);
    setEditing(true);
  };

  const saveEdit = async (): Promise<void> => {
    setBusy('save');
    setManageError(null);
    try {
      const updated = await updateEvent<EventDetailDto>(eventId, draft);
      setEvent(updated);
      setEditing(false);
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(null);
    }
  };

  const toggleRankings = async (): Promise<void> => {
    if (!event) return;
    setBusy('rankings');
    setManageError(null);
    try {
      setEvent(
        await updateEvent<EventDetailDto>(eventId, {
          excludeFromRankings: !event.excludeFromRankings,
        }),
      );
    } catch (err) {
      setManageError(
        err instanceof Error ? err.message : 'Could not change the rankings setting',
      );
    } finally {
      setBusy(null);
    }
  };

  const toggleCompression = async (): Promise<void> => {
    if (!event) return;
    setBusy('compress');
    setManageError(null);
    try {
      setEvent(
        await setEventCompressed<EventDetailDto>(eventId, !event.logCompressed),
      );
    } catch (err) {
      setManageError(
        err instanceof Error ? err.message : 'Could not change the log',
      );
    } finally {
      setBusy(null);
    }
  };

  const removeEvent = async (): Promise<void> => {
    setBusy('delete');
    setManageError(null);
    try {
      await deleteEvent(eventId);
      navigate('/events');
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Could not delete');
      setBusy(null);
      setConfirmingDelete(false);
    }
  };

  /**
   * The download is a browser navigation, so it cannot carry an Authorization
   * header — mint a 60-second token and put that in the URL instead.
   */
  const startRawDownload = async (): Promise<void> => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const token = await fetchDownloadToken();
      window.location.href =
        `${BACKEND_URL}/events/${eventId}/raw/download` +
        `?token=${encodeURIComponent(token)}`;
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : 'Could not start the download',
      );
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!rawOpen) return;
    let cancelled = false;
    const run = async (): Promise<void> => {
      setRawLoading(true);
      setRawError(null);
      try {
        const res = await authFetch(
          `/events/${eventId}/raw?limit=${RAW_PAGE_SIZE}&offset=${rawOffset}`,
        );
        const data: RawTeamUpdatesPage = await res.json();
        if (!cancelled) {
          setRawPage(data);
          setExpandedEntries(new Set());
        }
      } catch (err) {
        if (!cancelled) {
          setRawError(err instanceof Error ? err.message : 'Failed to load raw stream');
        }
      } finally {
        if (!cancelled) setRawLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [rawOpen, rawOffset, eventId]);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      try {
        const [evRes, lapsRes] = await Promise.all([
          authFetch(`/events/${eventId}`),
          authFetch(`/events/${eventId}/laps?limit=${LAP_SAMPLE_LIMIT}`),
        ]);
        const ev: EventDetailDto = await evRes.json();
        const ls: LapRecord[] = await lapsRes.json();
        if (!cancelled) {
          setEvent(ev);
          setLaps(ls);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load event');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  return (
    <div className="App">
      <div className="app-header">
        <h1>Karting Live Timing</h1>
        <SessionBadge />
      </div>
      <div className="app-main">
        <div className="main-content">
          <div className="events-page">
            <div className="events-toolbar">
              <button
                className="events-link-button"
                onClick={() => navigate('/events')}
              >
                ← Back to events
              </button>
              {event?.hasLog && (
                <button
                  className="events-link-button"
                  onClick={() => navigate(`/events/${eventId}/replay`)}
                  title="Play the recorded timing feed back"
                >
                  ▶ Replay
                </button>
              )}
              {isAdmin && event && (
                <>
                  <button
                    className="events-link-button"
                    onClick={beginEdit}
                    disabled={editing || busy !== null}
                  >
                    ✎ Rename
                  </button>
                  {event.hasLog && (
                    <button
                      className="events-link-button"
                      onClick={() => void toggleCompression()}
                      disabled={busy !== null}
                      title={
                        event.logCompressed
                          ? 'Expand the log so it can be replayed'
                          : 'Gzip the log to reclaim disk space'
                      }
                    >
                      {busy === 'compress'
                        ? 'Working…'
                        : event.logCompressed
                          ? '⤢ Decompress log'
                          : '⤡ Compress log'}
                    </button>
                  )}
                  <button
                    className="events-link-button"
                    onClick={() => void toggleRankings()}
                    disabled={busy !== null}
                    title={
                      event.excludeFromRankings
                        ? 'Count this event in the driver rankings again'
                        : 'Leave this event out of the driver rankings (practice, a broken feed)'
                    }
                  >
                    {busy === 'rankings'
                      ? 'Working…'
                      : event.excludeFromRankings
                        ? '★ Include in rankings'
                        : '☆ Exclude from rankings'}
                  </button>
                  <button
                    className="events-danger-button"
                    onClick={() => setConfirmingDelete(true)}
                    disabled={busy !== null}
                  >
                    🗑 Delete
                  </button>
                </>
              )}
            </div>

            {manageError && (
              <div className="events-error">Error: {manageError}</div>
            )}

            {editing && event && (
              <div className="events-edit-form">
                <label>
                  <span>Title</span>
                  <input
                    value={draft.title}
                    onChange={(e) =>
                      setDraft({ ...draft, title: e.target.value })
                    }
                    autoFocus
                  />
                </label>
                <label>
                  <span>Track</span>
                  <input
                    value={draft.trackName}
                    onChange={(e) =>
                      setDraft({ ...draft, trackName: e.target.value })
                    }
                  />
                </label>
                <label>
                  <span>Host</span>
                  <input
                    value={draft.host}
                    onChange={(e) => setDraft({ ...draft, host: e.target.value })}
                  />
                </label>
                <div className="events-confirm-actions">
                  <button
                    className="events-link-button"
                    onClick={() => void saveEdit()}
                    disabled={busy !== null}
                  >
                    {busy === 'save' ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="events-link-button"
                    onClick={() => setEditing(false)}
                    disabled={busy !== null}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {confirmingDelete && event && (
              <div className="events-confirm">
                <p>
                  Delete “{event.title}”, its {event.lapCount.toLocaleString()} lap
                  record{event.lapCount === 1 ? '' : 's'}
                  {event.hasLog &&
                    `, and its ${formatBytes(event.logFileSize)} raw log`}
                  ? This cannot be undone.
                </p>
                <div className="events-confirm-actions">
                  <button
                    className="events-danger-button"
                    onClick={() => void removeEvent()}
                    disabled={busy !== null}
                  >
                    {busy === 'delete' ? 'Deleting…' : 'Yes, delete'}
                  </button>
                  <button
                    className="events-link-button"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={busy !== null}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {loading && <div className="events-loading">Loading event…</div>}
            {error && <div className="events-error">Error: {error}</div>}

            {!loading && !error && event && (
              <>
                <div className="event-meta">
                  <h2 className="event-meta-title">{event.title}</h2>
                  <div className="event-meta-grid">
                    <div>
                      <span className="event-meta-label">Track</span>
                      <span className="event-meta-value">{event.trackName}</span>
                    </div>
                    <div>
                      <span className="event-meta-label">Host</span>
                      <span className="event-meta-value">
                        {event.host}:{event.wsPort}
                      </span>
                    </div>
                    <div>
                      <span className="event-meta-label">Started</span>
                      <span className="event-meta-value">
                        {formatDateTime(event.startedAt)}
                      </span>
                    </div>
                    <div>
                      <span className="event-meta-label">Ended</span>
                      <span className="event-meta-value">
                        {event.endedAt ? formatDateTime(event.endedAt) : '—'}
                      </span>
                    </div>
                    <div>
                      <span className="event-meta-label">Duration</span>
                      <span className="event-meta-value">
                        {formatDuration(event.durationMs)}
                      </span>
                    </div>
                    <div>
                      <span className="event-meta-label">Laps</span>
                      <span className="event-meta-value">{event.lapCount}</span>
                    </div>
                    <div>
                      <span className="event-meta-label">Rankings</span>
                      <span className="event-meta-value">
                        {event.excludeFromRankings
                          ? 'Excluded'
                          : event.rankedAt
                            ? 'Ranked'
                            : 'Pending'}
                      </span>
                    </div>
                    <div>
                      <span className="event-meta-label">Raw log</span>
                      <span className="event-meta-value">
                        {event.hasLog
                          ? `${formatBytes(event.logFileSize)}${
                              event.logCompressed ? ' (gzipped)' : ''
                            }`
                          : 'Missing'}
                      </span>
                    </div>
                  </div>
                </div>

                <EventStats eventId={eventId} />

                <h3 className="events-section-title">
                  Raw laps
                  <span className="es-sub">
                    {laps.length >= LAP_SAMPLE_LIMIT
                      ? `first ${LAP_SAMPLE_LIMIT} recorded`
                      : `${laps.length} recorded`}
                  </span>
                </h3>
                {laps.length === 0 ? (
                  <div className="events-empty">No laps recorded.</div>
                ) : (
                  <div className="events-table-wrapper">
                    <table className="events-table laps-table">
                      <thead>
                        <tr>
                          <th className="num">#</th>
                          <th className="num">Kart</th>
                          <th>Driver</th>
                          <th>Category</th>
                          <th className="num">Pos</th>
                          <th className="num">Lap</th>
                          <th className="num">Time</th>
                          <th className="num">S1</th>
                          <th className="num">S2</th>
                          <th className="num">S3</th>
                          <th className="num">Gap</th>
                          <th className="num">Int</th>
                          <th className="num">Best</th>
                          <th>Recorded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {laps.map((lap, idx) => (
                          <tr key={lap.id}>
                            <td className="num">{idx + 1}</td>
                            <td className="num">{lap.kartNumber}</td>
                            <td>{lap.driver}</td>
                            <td>{lap.category}</td>
                            <td className="num">{lap.position}</td>
                            <td className="num">{lap.lapNumber}</td>
                            <td className="num">{lap.lapTime}</td>
                            <td className="num">{lap.sector1}</td>
                            <td className="num">{lap.sector2}</td>
                            <td className="num">{lap.sector3}</td>
                            <td className="num">{lap.gap}</td>
                            <td className="num">{lap.interval}</td>
                            <td className="num">{lap.bestLap}</td>
                            <td>{formatDateTime(lap.recordedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="raw-section">
                  <div className="raw-section-header">
                    <h3 className="events-section-title">
                      Full team-update stream
                    </h3>
                    <div className="raw-section-actions">
                      <button
                        className="events-link-button"
                        type="button"
                        onClick={() => void startRawDownload()}
                        disabled={downloading}
                      >
                        {downloading ? 'Preparing…' : 'Download .jsonl'}
                      </button>
                      <button
                        className="events-link-button"
                        onClick={() => {
                          if (!rawOpen) {
                            setRawOffset(0);
                            setRawOpen(true);
                          } else {
                            setRawOpen(false);
                          }
                        }}
                      >
                        {rawOpen ? 'Hide stream' : 'Load stream'}
                      </button>
                    </div>
                  </div>

                  {downloadError && (
                    <div className="events-error">Error: {downloadError}</div>
                  )}

                  {rawOpen && (
                    <>
                      {rawLoading && !rawPage && (
                        <div className="events-loading">Loading raw stream…</div>
                      )}
                      {rawError && (
                        <div className="events-error">Error: {rawError}</div>
                      )}
                      {rawPage && (
                        <>
                          <div className="raw-meta">
                            <span>
                              {rawPage.total.toLocaleString()} entries
                            </span>
                            {rawPage.fileSize !== null && (
                              <span>{formatBytes(rawPage.fileSize)}</span>
                            )}
                            {rawPage.logFileName && (
                              <span className="raw-filename">
                                {rawPage.logFileName}
                              </span>
                            )}
                          </div>

                          {rawPage.total === 0 ? (
                            <div className="events-empty">
                              {rawPage.logFileName === null
                                ? 'No log file was recorded for this event.'
                                : rawPage.fileSize === 0
                                  ? 'The log file is empty — no data was received from the Apex server during the event (connection may have failed or timed out).'
                                  : 'No raw entries could be parsed from the log file.'}
                            </div>
                          ) : (
                            <>
                              <div className="raw-pager">
                                <button
                                  className="events-link-button"
                                  onClick={() =>
                                    setRawOffset(
                                      Math.max(0, rawOffset - RAW_PAGE_SIZE),
                                    )
                                  }
                                  disabled={rawOffset === 0 || rawLoading}
                                >
                                  ← Prev
                                </button>
                                <span className="raw-pager-info">
                                  {rawOffset + 1}–
                                  {Math.min(
                                    rawOffset + rawPage.entries.length,
                                    rawPage.total,
                                  )}{' '}
                                  / {rawPage.total}
                                </span>
                                <button
                                  className="events-link-button"
                                  onClick={() =>
                                    setRawOffset(rawOffset + RAW_PAGE_SIZE)
                                  }
                                  disabled={
                                    rawOffset + RAW_PAGE_SIZE >= rawPage.total ||
                                    rawLoading
                                  }
                                >
                                  Next →
                                </button>
                              </div>

                              <ol
                                className="raw-list"
                                start={rawOffset + 1}
                              >
                                {rawPage.entries.map((entry, i) => {
                                  const absoluteIdx = rawOffset + i;
                                  const expanded =
                                    expandedEntries.has(absoluteIdx);
                                  return (
                                    <li key={absoluteIdx} className="raw-item">
                                      <button
                                        className="raw-item-toggle"
                                        onClick={() => {
                                          setExpandedEntries((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(absoluteIdx)) {
                                              next.delete(absoluteIdx);
                                            } else {
                                              next.add(absoluteIdx);
                                            }
                                            return next;
                                          });
                                        }}
                                      >
                                        <span className="raw-item-ts">
                                          {formatTs(entry.t)}
                                        </span>
                                        <span
                                          className={`raw-item-kind raw-item-kind-${entry.kind || 'teamUpdate'}`}
                                        >
                                          {entry.kind || 'teamUpdate'}
                                        </span>
                                        <span className="raw-item-summary">
                                          {summariseEntry(entry)}
                                        </span>
                                        <span className="raw-item-caret">
                                          {expanded ? '▼' : '▶'}
                                        </span>
                                      </button>
                                      {expanded && (
                                        <pre className="raw-item-json">
                                          {JSON.stringify(entry.data, null, 2)}
                                        </pre>
                                      )}
                                    </li>
                                  );
                                })}
                              </ol>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
